import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Hub } from 'aws-amplify/utils'
import { useEffect } from 'react'
import { authKeys, useAuthStatus } from '@/data/hooks/useAuthStatus'
import { authService } from '@/data/services/auth/authService'
import { registerSessionExpiredHandler } from '@/data/services/auth/sessionEvents'
import { notifyError } from '@/data/services/error/errorNotifier'
import { clearLocalSession } from '../utils/clearLocalSession'

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.'

/**
 * Cognito session state plus sign-in/sign-out mutations.
 *
 * Cache hygiene: sign-in drops every non-auth query before the new user's
 * status loads, sign-out clears the cache and the browser storage that
 * describes the user's data, and a token refresh invalidates
 * the ['auth'] family so the permission profile is re-read.
 *
 * @returns {Object} Authentication state and methods
 */
export function useAuthQuery() {
  const queryClient = useQueryClient()
  const { data = {}, isPending } = useAuthStatus()
  const { isAuthenticated = false, user = null, token = null } = data

  const signInMutation = useMutation({
    mutationFn: (credentials) => authService.signIn(credentials),
    // The default mutation logger would print the credentials; LoginPage shows the error
    meta: { skipErrorLogging: true },
    onSuccess: () => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== authKeys.all[0] })
      // Returned so mutateAsync resolves once the new status is loaded
      return queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
  })

  const signOutMutation = useMutation({
    mutationFn: () => authService.signOut(),
    // Drop local data even if Cognito sign-out failed; the status query
    // rebuilds on the next render and redirects to login when appropriate
    onSettled: () => clearLocalSession(queryClient),
  })
  const { mutateAsync: signOutAsync } = signOutMutation

  useEffect(() => {
    const expire = async () => {
      notifyError(new Error(SESSION_EXPIRED_MESSAGE), { title: 'Signed out', severity: 'warning' })
      await signOutAsync().catch(() => {})
    }

    const unregister = registerSessionExpiredHandler(expire)
    const stopListening = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'tokenRefresh':
          queryClient.invalidateQueries({ queryKey: authKeys.all })
          break
        case 'tokenRefresh_failure':
          // Amplify keeps the tokens on a network error; only a rejected refresh ends the session
          if (payload.data?.error?.name !== 'NetworkError') expire()
          break
        case 'signedOut':
          clearLocalSession(queryClient)
          break
        default:
      }
    })

    return () => {
      unregister()
      stopListening()
    }
  }, [queryClient, signOutAsync])

  return {
    isAuthenticated,
    user,
    token,
    loading: isPending,
    signIn: signInMutation.mutateAsync,
    signOut: signOutAsync,
    isSigningIn: signInMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    getAuthToken: authService.getAuthToken,
  }
}
